import {
  Patient,
  CaseSlide,
  Block,
  Case,
  AccessionPrefix,
  CaseDocumentWithPresignedUrl,
  User,
  CaseReportSignature,
  PatientGuarantor,
  PatientInsurance,
  UserName,
  UserContact,
  UserCredentials,
  LabOrder,
  Slide,
} from "pwapi";

import { CaseDiagnosis, CaseAddon } from "custom-models";

import specimenDetailReducer, { SpecimenDetail } from "./specimenDetailReducer";
import {
  CASE_DETAIL_SUCCESS,
  CASE_DETAIL_FAILURE,
  CASE_DETAIL_EVENTS_SUCCESS,
  CASE_DETAIL_EVENTS_FAILURE,
  WIPE_CASE_DETAIL,
  LOGIN_SUCCESS,
  CASE_DETAIL_REQUEST,
  CASE_PATCH_SUCCESS,
  SLIDE_DETAIL_SUCCESS,
  CASE_REQUEST_SCREEN_SUCCESS,
  CASE_OPEN_DOCUMENT_MISSING,
  CASE_SIGN_OUT_FAILURE,
  CASE_SIGN_OUT_SUCCESS,
  DIAGNOSE_SUCCESS,
  UPDATE_DIAGNOSIS_SUCCESS,
  DIAGNOSE_FAILURE,
  UPDATE_DIAGNOSIS_FAILURE,
  UPDATE_DIAGNOSIS_REQUEST,
  DIAGNOSE_REQUEST,
  // WIPE_AI,
  CASE_DIAGNOSES_SUCCESS,
  OPTIMISTICALLY_EDIT_DIAGNOSIS,
  STAIN_ORDER_SUCCESS,
  DELETE_STAIN_ORDER_SUCCESS,
  CASE_PATCH_STATUS_SUCCESS,
  SET_MACRO_CODE,
  FLAG_CREATE_SUCCESS,
  CASE_REPORT_SUCCESS,
  UPDATE_CASE_ADDON_REQUEST,
  EDIT_SCREEN_DIAGNOSES_SUCCESS,
  CANCEL_EDIT_SCREEN_DIAGNOSES_SUCCESS,
  CASE_RECALL_REPORT_SUCCESS,
  CASE_PATIENT_MATCHES_SUCCESS,
  CASE_PATIENT_MATCHES_REQUEST,
  CASE_PATIENT_MATCHES_FAILURE,
  MODIFY_REPORT_SUCCESS,
  CANCEL_MODIFY_REPORT_SUCCESS,
  REPORT_SIGNEE_SUCCESS,
  UPDATE_CASE_SPECIMEN_SUCCESS,
  COMPLETE_STAIN_ORDER_SUCCESS,
  DELETE_DIAGNOSIS_SUCCESS,
  REVIEWS_SUCCESS,
  CREATE_NOTE_SUCCESS,
  PARTIAL_UPDATE_STAIN_ORDER_SUCCESS,
  UPDATE_CASE_DOCUMENT_SUCCESS,
  COPATH_CASE_PATIENT_MATCHES_SUCCESS,
  COPATH_CASE_PATIENT_MATCHES_FAILURE,
  COPATH_CASE_PATIENT_MATCHES_REQUEST,
} from "../actions/constants";

export type Modify<T, R> = Omit<T, keyof R> & R;

// TODO: Replace with openapi/models/Patient once we figure out the insurance
export interface CaseDetailPatient
  extends Modify<
    Patient,
    {
      first_name?: string;
      insurance?: PatientInsurance[];
      guarantor?: PatientGuarantor[];
      middle_initial?: string;
      ssn?: string;
    }
  > {}

// More restricted than CaseSlide interface above, appears in /api/cases/<pk>/events/ responses
export interface CaseLogItemSlide {
  id: number;
  lab_id: number;
  name: string;
  created_at: string;
  status: string;
  accession_number: string;
  block_obj: Block | null;
  slide_number: string | null;
}

export enum CaseLogEventType {
  RECEIVED_SIGN_OUT = "received-sign-out", // received WPL signout result message
  ACCESSION_DONE = "accession-done", // accessioning completed
  GROSS_DONE = "gross-done", // grossing completed
  RESCAN_ARCHIVAL = "rescan-archival", // archival rescan requested
  SLIDE_ADDED = "slide-added", // slide added to case
  ARCHIVED_BY_CLINIC_RULE = "archived-by-clinic-rule", // archived by clinic rule
  ASSIGN_SCREENER = "assign-screener", // screening pathologist assigned
  SCREEN_DONE = "screen-done", // screening completed
  ASSIGN_SIGNER = "assign-signer", // signing pathologist assigned
  SIGNED_OUT = "signed-out", // case signed out
}

export interface CaseLogItem {
  id: number;
  case: number;
  author: UserName | null;
  associated_user: UserName | null;
  event: CaseLogEventType;
  created_at: string;
  slide: CaseLogItemSlide | null;
}

export interface CaseDetailState
  extends Modify<
    Case,
    {
      // Accession became required when it was not before
      // (sept 25 merging master into ui/new-lis-ui --Evan)
      accession?: string;
      // TODO: These should not be required in openApi
      ordering_doctor?: UserContact;
      active_assignment?: UserCredentials;
      accession_prefix?: AccessionPrefix;
      screen_assignment?: UserName;
      finalize_assignment?: UserName;

      patient: CaseDetailPatient;
      specimenDetail: SpecimenDetail[];
      case_addon?: CaseAddon[];
      diagnosis?: string;
      lab_orders?: LabOrder[];
      slides: CaseSlide[];
      blocks?: Block[];
      loading: boolean;
      case_log: CaseLogItem[];
      documents: CaseDocumentWithPresignedUrl[];
      deadDocIds: number[];
      readonly case_diagnosis?: CaseDiagnosis[];
      error?: any;
      diagnosis_status?: "success" | "failed" | "loading" | "error";
      working_reasons?: any;
      report_signed_signature?: CaseReportSignature;
      report_signee?: User;

      patient_matches?: Case[];
      copath_patient_matches?: string[];
      qa_reviews_list: any[];
      disclaimers?: Array<{ type?: string; disclaimer?: string }>;
    }
  > {}

export const initialState: CaseDetailState = {
  slides: [],
  specimens: [],
  specimenDetail: [],
  patient: {},
  documents: [],
  loading: true,
  case_log: [],
  case_diagnosis: undefined,
  deadDocIds: [],
  lab_orders: [],
  qa_reviews: [],
  qa_reviews_list: [],
  screen_requests: [],
};

const reducer = (
  state: CaseDetailState = initialState,
  action: any
): CaseDetailState => {
  switch (action.type) {
    case CASE_DETAIL_REQUEST:
      return {
        ...state,
        loading: true,
      };
    case REPORT_SIGNEE_SUCCESS:
      return {
        ...state,
        report_signee: action.payload.response || {},
      };
    case MODIFY_REPORT_SUCCESS:
    case CANCEL_MODIFY_REPORT_SUCCESS:
    case EDIT_SCREEN_DIAGNOSES_SUCCESS:
    case CANCEL_EDIT_SCREEN_DIAGNOSES_SUCCESS:
    case CASE_RECALL_REPORT_SUCCESS:
    case CASE_DETAIL_SUCCESS:
      return {
        ...state,
        ...action.payload,
        specimenDetail: specimenDetailReducer(state.specimenDetail, action),
        loading: false,
      };
    case CASE_REPORT_SUCCESS:
      const serialization = action.payload.response.serialization;
      return {
        ...state,
        ...serialization,
        status: Case.status.FINALIZED,
        specimenDetail: specimenDetailReducer(state.specimenDetail, action),
        loading: false,
      };
    case UPDATE_CASE_ADDON_REQUEST:
      return state.case_addon
        ? {
            ...state,
            case_addon: state.case_addon.map((addon) =>
              addon.id === action.payload.id
                ? {
                    ...addon,
                    content: action.payload.content,
                  }
                : addon
            ),
          }
        : state;
    case CASE_PATCH_SUCCESS:
      return {
        ...state,
        ...action.payload, // includes slides field from server
        loading: false,
      };
    case CASE_PATCH_STATUS_SUCCESS:
      return {
        ...state,
        status: action.payload.status,
      };
    case CASE_DETAIL_FAILURE:
      return {
        ...state,
        loading: false,
        error: action.payload,
      };
    case CASE_DETAIL_EVENTS_SUCCESS:
      return {
        ...state,
        case_log: action.payload,
      };
    case CASE_DETAIL_EVENTS_FAILURE:
      return {
        ...state,
        error: action.payload,
      };
    case CASE_PATIENT_MATCHES_SUCCESS:
      return {
        ...state,
        patient_matches: action.payload?.response,
      };
    case CASE_PATIENT_MATCHES_REQUEST:
    case CASE_PATIENT_MATCHES_FAILURE:
      return {
        ...state,
        patient_matches: undefined,
      };
    case COPATH_CASE_PATIENT_MATCHES_SUCCESS:
      return {
        ...state,
        copath_patient_matches: action.payload?.response,
      };
    case COPATH_CASE_PATIENT_MATCHES_REQUEST:
    case COPATH_CASE_PATIENT_MATCHES_FAILURE:
      return {
        ...state,
        copath_patient_matches: undefined,
      };
    case WIPE_CASE_DETAIL:
      return { ...initialState };
    case LOGIN_SUCCESS:
      return {
        ...state,
        error: undefined,
      };
    case SLIDE_DETAIL_SUCCESS:
      return {
        ...state,
        slides: state.slides.map((s: CaseSlide) =>
          s.id === action.payload.id
            ? {
                ...s,
                was_previously_viewed:
                  s.status === Slide.status.SUCCESS ||
                  !!action._meta.canViewNonSuccessSlides,
              }
            : s
        ),
      };
    case CASE_DIAGNOSES_SUCCESS:
      return {
        ...state,
        specimenDetail: specimenDetailReducer(state.specimenDetail, action),
      };
    case DIAGNOSE_REQUEST:
      return {
        ...state,
        diagnosis_status: "loading",
      };
    case DIAGNOSE_SUCCESS:
      return {
        ...state,
        case_diagnosis: (state.case_diagnosis || []).concat([
          action.payload.caseDiagnosis,
        ]),
        specimenDetail: specimenDetailReducer(state.specimenDetail, action),
        diagnosis_status: "success",
      };
    case DIAGNOSE_FAILURE:
      return {
        ...state,
        diagnosis_status: "failed",
      };
    case UPDATE_DIAGNOSIS_REQUEST: {
      return {
        ...state,
        diagnosis_status: "loading",
      };
    }
    case UPDATE_DIAGNOSIS_SUCCESS: {
      return {
        ...state,
        case_diagnosis: state.case_diagnosis
          ? state.case_diagnosis.map((diagnosis) => {
              if (action.payload.caseDiagnosis.id === diagnosis.id) {
                return {
                  ...action.payload.caseDiagnosis,
                  id: diagnosis.id,
                };
              }
              return diagnosis;
            })
          : undefined,
        specimenDetail: specimenDetailReducer(state.specimenDetail, action),
        diagnosis_status: "success",
      };
    }
    case UPDATE_DIAGNOSIS_FAILURE: {
      return {
        ...state,
        diagnosis_status: "error",
      };
    }
    case DELETE_DIAGNOSIS_SUCCESS: {
      return {
        ...state,
        case_diagnosis: state.case_diagnosis
          ? state.case_diagnosis.filter(
              (diagnosis) => diagnosis.id !== action.args.id
            )
          : undefined,
        specimenDetail: specimenDetailReducer(state.specimenDetail, action),
        diagnosis_status: "success",
      };
    }
    case CASE_REQUEST_SCREEN_SUCCESS:
      return {
        ...state,
        active_assignment: action.payload.response.active_assignment,
        screen_assignment: action.payload.response.screen_assignment,
        finalize_assignment: action.payload.response.finalize_assignment,
        is_screening: action.payload.response.is_screening,
      };
    case CASE_SIGN_OUT_SUCCESS:
      return {
        ...state,
        error: undefined,
      };
    case CASE_SIGN_OUT_FAILURE:
      return {
        ...state,
        error: action.payload,
      };
    case CASE_OPEN_DOCUMENT_MISSING:
      return {
        ...state,
        deadDocIds: [...state.deadDocIds, action.payload],
      };
    case OPTIMISTICALLY_EDIT_DIAGNOSIS:
      return {
        ...state,
        diagnosis_status: "loading",
        specimenDetail: specimenDetailReducer(state.specimenDetail, action),
      };
    case STAIN_ORDER_SUCCESS:
      const lab_orders = Array.isArray(action.payload.response)
        ? [...action.payload.response]
        : [action.payload.response];
      return {
        ...state,
        lab_orders: (state.lab_orders || []).concat(lab_orders),
      };
    case COMPLETE_STAIN_ORDER_SUCCESS:
      return {
        ...state,
        lab_orders: (state.lab_orders || []).map((lab_order) =>
          lab_order.id !== action.args
            ? lab_order
            : {
                ...lab_order,
                completed_at: action.payload.response.completed_at,
              }
        ),
      };
    case DELETE_STAIN_ORDER_SUCCESS:
      return {
        ...state,
        lab_orders: (state.lab_orders || []).map((lab_order) =>
          lab_order.id !== action.args.id
            ? lab_order
            : {
                ...lab_order,
                ...action.payload.response,
              }
        ),
      };
    case PARTIAL_UPDATE_STAIN_ORDER_SUCCESS:
      return {
        ...state,
        lab_orders: (state.lab_orders || []).map((lab_order) =>
          lab_order.id !== action.payload.response.id
            ? lab_order
            : {
                ...lab_order,
                ...action.payload.response,
              }
        ),
      };
    case SET_MACRO_CODE:
      return {
        ...state,
        specimenDetail: specimenDetailReducer(state.specimenDetail, action),
      };
    case UPDATE_CASE_SPECIMEN_SUCCESS:
      return state && action.payload.response.id
        ? {
            ...state,
            specimens: state.specimens
              ? state.specimens.map((s) =>
                  s.id === action.payload.response.id
                    ? { ...s, ...action.payload.response }
                    : s
                )
              : [action.payload.response],
            specimenDetail: specimenDetailReducer(state.specimenDetail, action),
          }
        : state;
    case UPDATE_CASE_DOCUMENT_SUCCESS:
      return state && action.payload.response.id
        ? {
            ...state,
            documents: state.documents
              ? state.documents.map((d) =>
                  d.id === action.payload.response.id
                    ? { ...d, ...action.payload.response }
                    : d
                )
              : state.documents,
          }
        : state;
    case FLAG_CREATE_SUCCESS:
      const alteredSlideIndex = state.slides.findIndex(
        (s) => s.id === action.payload.result.slide_id
      );
      return alteredSlideIndex !== -1
        ? {
            ...state,
            ...(action.payload.result.order && {
              lab_orders: [
                ...(state.lab_orders || []),
                action.payload.result.order,
              ],
            }),
            slides: [
              ...state.slides.slice(0, alteredSlideIndex),
              ...(!action.payload.isMislabel
                ? [
                    {
                      ...state.slides[alteredSlideIndex],
                      flags: [
                        ...(state.slides[alteredSlideIndex].flags || []),
                        action.payload.result,
                      ],
                    },
                  ]
                : []),
              ...state.slides.slice(alteredSlideIndex + 1),
            ],
          }
        : state;
    case REVIEWS_SUCCESS:
      return {
        ...state,
        qa_reviews_list: action.payload.response.results,
      };
    case CREATE_NOTE_SUCCESS:
      return {
        ...state,
        qa_reviews_list: [
          action.payload.response,
          ...(state.qa_reviews_list || []),
        ],
      };
    default:
      return state;
  }
};

export default reducer;
